import React, { useState, useEffect } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as XLSX from "xlsx";
import { fetchAllClients } from "../Redux/WhatsappSlice/Client";
import {
  AddNewPlan,
  DeletePlan,
  fetchPlanData,
} from "../Redux/GeminiOCRSlice/GeminiOCR";
import WpDynamicTable from "../Components/WhatsappTable/WpDynamicTable";
import ConfirmModal from "../Components/ConfirmModal/ConfirmModal";

const AddPlan = () => {
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    // If it matches the format we are saving, return as is (or reformat if needed)
    if (moment(dateStr, "DD-MM-YYYY HH:mm:ss", true).isValid()) {
      return dateStr;
    }
    // Otherwise try to identify it as a standard date and format it
    const m = moment(dateStr);
    if (m.isValid()) {
      return m.format("DD-MM-YYYY HH:mm:ss");
    }
    return dateStr;
  };

  const dispatch = useDispatch();

  const { AllClients } = useSelector((state) => state.Client);
  const { PlanData } = useSelector((state) => state.GeminiOCR);

  const [formData, setFormData] = useState({
    ClientID: "",
    Amount: "",
    Price: "",
  });

  const [apiCount, setApiCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // row to delete

  // Client Name filter state
  const [clientNameFilter, setClientNameFilter] = useState("");

  // Filtered rows state
  const [filteredRows, setFilteredRows] = useState([]);

  useEffect(() => {
    dispatch(fetchAllClients());
    dispatch(fetchPlanData());
  }, [dispatch]);

  useEffect(() => {
    if (formData.Amount && formData.Price) {
      const amount = parseFloat(formData.Amount);
      const price = parseFloat(formData.Price);

      if (price > 0) {
        const count = Math.floor(amount / price);
        setApiCount(count);
      } else {
        setApiCount(0);
      }
    } else {
      setApiCount(0);
    }
  }, [formData.Amount, formData.Price]);

  // Apply filter whenever PlanData or clientNameFilter changes
  useEffect(() => {
    const filtered = (PlanData || [])
      ?.map((el, index) => ({
        id: index + 1, // Original ID before filtering
        MainId: el._id,
        WhaClientName: el?.ClientID?.WhaClientName,
        Amount: el?.Amount,
        BalAmount: el?.BalAmount,
        Price: el?.Price,
        BalApiCount: el?.BalApiCount,
        TotalApiCount: el?.TotalApiCount,
        Date: formatDisplayDate(el?.Date),
      }))
      ?.filter((row) =>
        clientNameFilter === "" || row.WhaClientName === clientNameFilter
      )
      ?.map((row, index) => ({
        ...row,
        id: index + 1, // Reassign sequential numbers after filtering
      }));

    setFilteredRows(filtered || []);
  }, [PlanData, clientNameFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Client Name filter change handler
  const handleClientNameFilterChange = (e) => {
    setClientNameFilter(e.target.value);
  };

  const validate = (formData) => {
    const errors = {};

    if (!formData.ClientID || formData.ClientID.length === 0) {
      errors.ClientID = "Client name is required";
    }

    if (!formData.Amount || formData.Amount.length === 0) {
      errors.Amount = "Amount is required";
    } else if (isNaN(formData.Amount) || parseFloat(formData.Amount) <= 0) {
      errors.Amount = "Amount must be a valid number greater than 0";
    }

    if (!formData.Price || formData.Price.length === 0) {
      errors.Price = "Price is required";
    } else if (isNaN(formData.Price) || parseFloat(formData.Price) <= 0) {
      errors.Price = "Price must be a valid number greater than 0";
    }

    return errors;
  };

  const handleDelete = (row) => {
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(DeletePlan({ id: deleteTarget.MainId })).unwrap();
      dispatch(fetchPlanData());
      toast.success("Plan deleted successfully!", { position: "top-right", autoClose: 3000 });
    } catch (error) {
      toast.error("Failed to delete plan.", { position: "top-right", autoClose: 3000 });
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);

      const submitData = {
        ClientID: formData.ClientID,
        Amount: parseFloat(formData.Amount),
        Price: parseFloat(formData.Price),
        Date: moment().format("DD-MM-YYYY HH:mm:ss"),
      };

      try {
        await dispatch(AddNewPlan({ values: submitData })).unwrap();
        dispatch(fetchPlanData());
        setFormData({
          ClientID: "",
          Amount: "",
          Price: "",
        });
        setApiCount(0);
        toast.success("Plan added successfully!", { position: "top-right", autoClose: 3000 });
      } catch (error) {
        toast.error("Failed to add plan. Please try again.", { position: "top-right", autoClose: 3000 });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      toast.warn("Please fix the validation errors before submitting.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Function to download Excel file
  const downloadExcel = () => {
    if (filteredRows.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // Prepare data for Excel (excluding MainId and adding header names)
      const excelData = filteredRows.map((row, index) => ({
        "No": row.id || index + 1,
        "Client Name": row.WhaClientName || "-",
        "Price": row.Price ?? "-",
        "Balance API Count": row.BalApiCount ?? "-",
        "Total API Count": row.TotalApiCount ?? "-",
        "Date-Time": row.Date || "-",
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Plans");

      // Set column widths
      const colWidths = [
        { wch: 5 },   // No
        { wch: 25 },  // Client Name
        { wch: 15 },  // Price
        { wch: 20 },  // Balance API Count
        { wch: 20 },  // Total API Count
        { wch: 20 },  // Date-Time
      ];
      worksheet["!cols"] = colWidths;

      // Generate file name with timestamp
      const fileName = `Plan_Data_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, fileName);

      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download Excel file");
    }
  };

  const columns = [
    { field: "id", headerName: "No", flex: 1 },
    { field: "WhaClientName", headerName: "Client Name", flex: 1 },
    { field: "Amount", headerName: "Amount", flex: 1 },
    { field: "BalAmount", headerName: "Balance Amount", flex: 1 },
    { field: "Price", headerName: "Price", flex: 1 },
    { field: "BalApiCount", headerName: "Bal Api Count", flex: 1 },
    { field: "TotalApiCount", headerName: "Total Api Count", flex: 1 },
    { field: "Date", headerName: "Date-Time", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Plan"
        message={`Are you sure you want to delete the plan for "${deleteTarget?.WhaClientName}"? This action cannot be undone.`}
      />
      <div className="">
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            Add Plan
          </h3>
        </div>

        <div className="Form_bg_color bg-white shadow-sm rounded-xl mt-4">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-12 gap-4 p-3 rounded-2xl"
          >
            {/* Client Name Dropdown */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Client Name</h5>
              <select
                name="ClientID"
                value={formData.ClientID}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-[10px] border-2 border-black rounded"
                disabled={isSubmitting}
              >
                <option value="">Select Client</option>
                {[...(AllClients?.data || [])]
                  .sort((a, b) => (a.WhaClientName || "").localeCompare(b.WhaClientName || ""))
                  .map((client) => (
                    <option key={client?._id} value={client?._id}>
                      {client.WhaClientName}
                    </option>
                  ))}
              </select>
              {errors.ClientID && (
                <p className="text-red-500 text-sm">{errors.ClientID}</p>
              )}
            </div>

            {/* Amount Input */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Amount</h5>
              <input
                type="number"
                name="Amount"
                value={formData.Amount}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                min="0"
                step="0.01"
                disabled={isSubmitting}
              />
              {errors.Amount && (
                <p className="text-red-500 text-sm">{errors.Amount}</p>
              )}
            </div>

            {/* Price Input */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Price</h5>
              <input
                type="number"
                name="Price"
                value={formData.Price}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                min="0"
                step="0.01"
                disabled={isSubmitting}
              />
              {errors.Price && (
                <p className="text-red-500 text-sm">{errors.Price}</p>
              )}
            </div>

            {/* API Count (Read Only) */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Total API Count</h5>
              <input
                type="text"
                value={apiCount}
                readOnly
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded cursor-not-allowed"
              />
            </div>

            {/* Submit Button */}
            <div className="submit_Form col-span-12 flex justify-center">
              <button
                type="submit"
                className="All_button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </div>

        <div className="px-2 py-6">
          {/* Filter + Download Button Row */}
          <div className="mb-4 mt-3 flex flex-col md:flex-row gap-4 items-end md:items-end justify-between">

            {/* Client Name Filter */}
            <div className="w-full md:w-60">
              <label className="block text-sm font-medium mb-1">
                Filter by Client Name
              </label>
              <select
                value={clientNameFilter}
                onChange={handleClientNameFilterChange}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">All Client Names</option>
                {[
                  ...new Set(
                    (Array.isArray(PlanData) ? PlanData : []).map(
                      (el) => el?.ClientID?.WhaClientName
                    )
                  ),
                ]
                  .filter(Boolean)
                  .sort((a, b) => a.localeCompare(b))
                  .map((name, idx) => (
                    <option key={idx} value={name}>
                      {name}
                    </option>
                  ))}
              </select>
            </div>
            <button
              onClick={downloadExcel}
              className="bg-[#122A47] text-white py-2 px-4 rounded hover:bg-[#0c2545]"
              disabled={filteredRows.length === 0}
            >
              Download Excel
            </button>
          </div>

          {/* Table */}
          <div className="rounded-lg overflow-x-auto mt-3">
            <WpDynamicTable columns={columns} rows={filteredRows} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPlan;